import { defineStore, storeToRefs } from "pinia";
import { ref, computed } from "vue";
import { useSurveyStore } from "@stores/survey/survey";
import { useAsyncJob } from "@/composables/useAsyncJob";
import { createReportsV2Api } from "./reportsV2.api";
import { createSlidesManager } from "./reportsV2.slides";
import { captureEvent } from "@/lib/analytics";

export const useReportsV2Store = defineStore("reportsV2", () => {
  const surveyStore = useSurveyStore();
  const { token: surveyToken } = storeToRefs(surveyStore);

  // Core state
  const reports = ref([]);
  const currentReport = ref(null);
  const selectedSlide = ref(null);
  const loading = ref(false);
  const saving = ref(false);
  const generating = ref(false);
  const searchQuery = ref("");
  // Initialize API module
  const api = createReportsV2Api(surveyToken, currentReport, reports);

  // Initialize slides manager
  const slides = createSlidesManager(currentReport, selectedSlide, api);

  // Computed properties
  const filteredReports = computed(() => {
    if (!searchQuery.value) {
      return reports.value;
    }
    const query = searchQuery.value.toLowerCase();
    return reports.value.filter((report) => report.name?.toLowerCase().includes(query));
  });

  const isReady = computed(() => currentReport.value?.status === "ready");

  // Report CRUD operations
  async function fetchReports() {
    await api.fetchReports(loading);
  }

  async function fetchReport(reportToken) {
    loading.value = true;
    const { response, data } = await api.fetchReportData(reportToken);

    if (response.value.ok) {
      currentReport.value = data.value;
      if (data.value.slides?.length > 0) {
        selectedSlide.value = data.value.slides[0];
      }
      slides.resetDirtyState();
    }
    loading.value = false;
    return currentReport.value;
  }

  async function createReport(params) {
    const { response, data } = await api.createReportData(params);

    if (response.value.ok) {
      reports.value.unshift(data.value);
      currentReport.value = data.value;
      selectedSlide.value = null;
      slides.resetDirtyState();
      return data.value;
    } else {
      throw new Error(data.value?.error || "Failed to create report");
    }
  }

  async function updateReport(params) {
    const { response, data } = await api.updateReportData(params);

    if (response.value.ok) {
      currentReport.value = { ...currentReport.value, ...data.value };
      const index = reports.value.findIndex((r) => r.token === data.value.token);
      if (index !== -1) {
        reports.value[index] = data.value;
      }
      return data.value;
    } else {
      throw new Error(data.value?.error || "Failed to update report");
    }
  }

  async function deleteReport(reportToken) {
    const { response } = await api.deleteReportData(reportToken);

    if (response.value.ok) {
      reports.value = reports.value.filter((r) => r.token !== reportToken);
      if (currentReport.value?.token === reportToken) {
        currentReport.value = null;
        selectedSlide.value = null;
      }
    }
  }

  async function duplicateReportFull(reportToken) {
    const { response, data } = await api.duplicateReportData(reportToken);

    if (response.value.ok) {
      reports.value.unshift(data.value);
      return data.value;
    } else {
      throw new Error(data.value?.error || "Failed to duplicate report");
    }
  }

  // Save report (delegates to slides manager)
  async function persistReport() {
    const saved = await slides.saveAllSlides(saving);
    return saved;
  }

  async function saveReport() {
    const saved = await persistReport();

    if (saved) captureEvent("report_saved", { report_token: currentReport.value?.token });
  }

  // Generation
  const asyncJob = useAsyncJob();

  async function generateReport(cableInstance = null, vueInstance = null) {
    if (!cableInstance) {
      console.error("generateReport: cable instance is required");
      return;
    }

    generating.value = true;

    if (slides.hasUnsavedChanges.value) {
      await persistReport();
    }

    try {
      await asyncJob.execute({
        cable: cableInstance,
        vueInstance,
        channel: "ReportsV2Channel",
        channelParams: { survey_token: surveyToken.value },
        httpCall: async (clientId) => {
          return await api.generateReportData(clientId);
        },
      });

      currentReport.value.status = "ready";
      currentReport.value.has_generated_file = true;
      const currentSlideId = selectedSlide.value?.id;
      await fetchReport(currentReport.value.token);
      if (currentSlideId) {
        slides.selectSlide(currentSlideId);
      }
      // Reset dirty state again after slide selection, in case any component
      // emitted updates during the re-render (e.g., MiniRichEditor normalizing HTML)
      slides.resetDirtyState();
      downloadReport();
    } catch (error) {
      currentReport.value.status = "error";
      console.error("Report generation failed:", error);
    } finally {
      generating.value = false;
    }
  }

  function downloadReport() {
    const url = api.getDownloadUrl();
    const link = document.createElement("a");
    link.href = url;
    link.download = `${currentReport.value.name || "report"}.pptx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function resetFilters() {
    searchQuery.value = "";
  }

  return {
    // Core state
    reports,
    currentReport,
    selectedSlide,
    loading,
    saving,
    generating,
    searchQuery,
    filteredReports,
    isReady,

    // Slides state (re-exported from slides manager)
    hasUnsavedChanges: slides.hasUnsavedChanges,
    hasSlides: slides.hasSlides,
    dirtyBindings: slides.dirtyBindings,


    // Report actions
    fetchReports,
    fetchReport,
    createReport,
    updateReport,
    deleteReport,
    duplicateReportFull,
    saveReport,
    resetFilters,

    // Slide getters (re-exported from slides manager)
    getEffectiveBindings: slides.getEffectiveBindings,

    // Slide actions (re-exported from slides manager)
    addSlide: slides.addSlide,
    addSlideAfter: slides.addSlideAfter,
    duplicateSlide: slides.duplicateSlide,
    updateSlideLocal: slides.updateSlideLocal,
    removeSlide: slides.removeSlide,
    reorderSlides: slides.reorderSlides,
    selectSlide: slides.selectSlide,


    // Generation actions
    generateReport,
    getDownloadUrl: api.getDownloadUrl,
    downloadReport,
  };
});
